import {
  Download,
  FileText,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import { apiService, extractApiError } from "../services/api";
import type { SecurityAdvisory, SecurityAdvisoryListResponse } from "../types";
import { printCsafPdf } from "../utils/csafPdf";
import { validateCsaf } from "../utils/csafValidator";

export default function SecurityAdvisoriesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: advisoryResponse, isLoading } = useQuery(
    ["security-advisories"],
    () => apiService.getSecurityAdvisories({ page: 1, limit: 50 }),
  );

  const advisories =
    (advisoryResponse?.data?.data as SecurityAdvisoryListResponse | undefined)
      ?.items || [];

  const deleteMutation = useMutation(
    (id: string) => apiService.deleteSecurityAdvisory(id),
    {
      onSuccess: () => {
        toast.success("Security advisory deleted");
        queryClient.invalidateQueries(["security-advisories"]);
      },
      onError: (error: unknown) => {
        toast.error(
          extractApiError(error, "Failed to delete security advisory"),
        );
      },
    },
  );

  const onExport = async (id: string) => {
    try {
      const response = await apiService.exportSecurityAdvisoryCsaf(id);
      const blob = new Blob([response.data], { type: "application/json" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const contentDisposition = response.headers["content-disposition"] as
        | string
        | undefined;
      const filename =
        contentDisposition?.split("filename=")?.[1]?.replace(/"/g, "") ||
        `security-advisory-${id}.json`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error(extractApiError(error, "Failed to export CSAF file"));
    }
  };

  const onExportPdf = (item: SecurityAdvisory) => {
    const result = validateCsaf(item.csaf);
    if (!result.valid) {
      toast.error(
        `Cannot export PDF — advisory has ${result.errors.length} CSAF validation issue${
          result.errors.length === 1 ? "" : "s"
        }`,
      );
      return;
    }
    if (!printCsafPdf(item.csaf)) {
      toast.error("Unable to open the PDF print view");
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Security Advisories
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Author CSAF 2.0 advisories with a guided form or raw JSON, then
            export CSAF and a Sandvik-branded PDF.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary shrink-0"
          onClick={() => navigate("/security-advisories/create")}
        >
          <Plus className="h-4 w-4 mr-2" />
          New advisory
        </button>
      </div>

      {advisories.length === 0 ? (
        <div className="card p-12 text-center">
          <ShieldCheck className="h-10 w-10 mx-auto text-gray-400 dark:text-gray-500" />
          <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-gray-100">
            No security advisories yet
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create your first CSAF 2.0 advisory to get started.
          </p>
          <button
            type="button"
            className="btn btn-primary mt-6"
            onClick={() => navigate("/security-advisories/create")}
          >
            <Plus className="h-4 w-4 mr-2" />
            New advisory
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Scope
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {advisories.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                  onClick={() =>
                    navigate(`/security-advisories/${item.id}/edit`)
                  }
                >
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {item.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.businessArea} / {item.division}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.status === "final"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                          : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.version}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {new Date(item.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: stops row navigation for action buttons */}
                    <div
                      className="inline-flex items-center justify-end gap-1 flex-nowrap whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                        title="Edit this advisory"
                        onClick={() =>
                          navigate(`/security-advisories/${item.id}/edit`)
                        }
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                        title="Download the CSAF 2.0 JSON file"
                        onClick={() => onExport(item.id)}
                      >
                        <Download className="h-3.5 w-3.5" />
                        CSAF
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-900 dark:border-white bg-slate-900 dark:bg-white px-3 py-1.5 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors"
                        title="Download the branded PDF advisory"
                        onClick={() => onExportPdf(item)}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        PDF
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 hover:border-rose-200 dark:hover:border-rose-800 transition-colors"
                        title="Delete this advisory"
                        onClick={() => deleteMutation.mutate(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
